import { reactive } from 'vue'
import * as echarts from 'echarts'
import dotImg from '@assets/images/capability_dot.png'

export default function useFlightEchartOptions(containerId = 'echartBox'){
    const state = reactive({
        echart: null as echarts.EChartsType | null,
        containerId,
        options: {
                title: {
                text: ''
            },
            legend: {
                data: []
            },
            radar: {
                shape: 'polygon', // 多边形（默认），也可以是 'circle'
                radius: '64%',
                nameGap: 8,
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(8, 77, 205, 1)',
                        width: 1
                    },
                },
                axisName: {
                    show: true,
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: 14,
                    fontFamily: 'HarmonyOS Sans SC'
                },
                axisLabel: {
                    customValues: [0, 2, 4, 6, 8, 10],
                    color: 'rgba(171, 202, 255, 1)',
                    verticalAlign: 'middle',
                    align: 'right',
                    rotate: 0,
                    fontSize: 12,
                    height: 16,
                    fontFamily: 'HarmonyOS Sans SC'
                },
                indicator: [
                    { name: '飞行稳定性', min: 0, max: 10, axisLabel: { show: false } },
                    { name: '偏差控制', min: 0, max: 10, axisLabel: { show: false } },
                    { name: '纠错反应 ', min: 0, max: 10, axisLabel: { show: false } },
                    { name: '抗干扰', min: 0, max: 10, axisLabel: { show: false } },
                    { name: '打杆熟练度', min: 0, max: 10, axisLabel: { show: false } },
                ],
                splitArea: {
                    areaStyle: {
                        color: '#20232B',
                        shadowBlur: 20,
                        shadowColor: '#2174FF',
                        shadowOffsetX: 0,
                        shadowOffsetY: 0,
                        opacity: 0.2
                    }
                },
                splitLine: {
                    lineStyle: {
                        // 使用深浅的间隔色
                        color: ['#2174FF', '#084DCD', '#084DCD', '#084DCD', '#084DCD'],
                    }
                }
            },
            series: [
                {
                    name: '能力分析',
                    type: 'radar',
                    data: [
                        {
                            value: [0, 0, 0, 0, 0],
                            name: '能力分析',
                            areaStyle: { // 填充区域的样式（对应你图中的蓝色填充）
                                color: 'rgba(33, 116, 255, 0.8)' // 半透明蓝色
                            },
                            lineStyle: { // 轮廓线的样式
                                color: 'rgba(33, 116, 255, 1)'
                            },
                            symbol: 'image://' + dotImg, // 数据点的形状（对应你图中的白色圆点）
                            symbolSize: 8, // 数据点大小
                        },
                    ]
                }
            ]
        },
    })
    
    /**
     * 初始化 echarts
     * @param containerId 容器 DOM ID，默认 'echartBox'
     * @param options 可选配置项
     */
    const initEcharts = (containerId = 'echartBox') => {
        const echartBox = document.getElementById(containerId) as HTMLElement
        if (echartBox) {
            state.echart = echarts.init(echartBox)
            state.echart.setOption(state.options)
        }
    }
    
    /**
     * 调整 echarts 尺寸
     */
    const resizeEcharts = () => {
        state.echart?.resize()
    }
    
    /**
     * 销毁 echarts 实例
     */
    const disposeEcharts = () => {
        state.echart?.dispose()
        state.echart = null
    }
    
    /**
     * 获取当前配置（便于外部修改）
     */
    const getOptions = () => {
        return state.options
    }
    
    /**
     * 设置新的配置
     */
    const setOptions = (options: any) => {
        state.echart?.setOption(options)
    }

    /**
     * 更新雷达图数据
     * @param radarData 雷达图数据，范围 [0, 1]
     */
    const updateRadarData = (radarData: {
        flight_stability?: number,      // 飞行稳定性
        stick_proficiency?: number,     // 打杆熟练度
        anti_disturbance?: number,      // 抗干扰
        error_correction?: number,      // 纠错反应
        deviation_control?: number      //偏差控制
    }) => {
        if (!radarData || !state.echart) return

        // 将 0-1 范围转换为 0-10
        const values = [
            (radarData.flight_stability ?? 0) * 10,
            (radarData.deviation_control ?? 0) * 10,
            (radarData.error_correction ?? 0) * 10,
            (radarData.anti_disturbance ?? 0) * 10,
            (radarData.stick_proficiency ?? 0) * 10,
        ]

        // 5 维全 0 视为后端未给出有效评分（insufficient_data / pass_fail_gate 无 issue），
        // 隐藏数据多边形并显示占位文案，避免画面坍缩到圆心造成误读
        const isEmpty = values.every(v => !Number.isFinite(v) || v <= 0)

        const newOptions = {
            ...state.options,
            graphic: isEmpty ? [{
                type: 'text',
                left: 'center',
                top: 'middle',
                style: {
                    text: '',
                    fill: 'rgba(171, 202, 255, 0.85)',
                    fontSize: 14,
                    fontFamily: 'HarmonyOS Sans SC'
                }
            }] : [],
            series: [{
                ...state.options.series[0],
                data: isEmpty ? [] : [{
                    ...state.options.series[0].data[0],
                    value: values,
                }]
            }]
        }

        state.echart.setOption(newOptions, { notMerge: true })
    }

    return {
        ...state,
        initEcharts,
        resizeEcharts,
        disposeEcharts,
        getOptions,
        setOptions,
        updateRadarData
    }
}
